import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { searchAddress, updateAddress } from "@/redux/ducks/customer";
import { Props } from "@/pages/register/Models/address";
import { getIsValidString } from "@/utils/functions";
import { NavigateFunction } from "react-router-dom";

const addressSchema = z.object({
  zipcode: z
    .string()
    .regex(/^\d{5}-\d{3}$/, "Cep inválido")
    .refine((val) => val.length === 9, "Cep inválido"),
  streetName: z.string().min(1, "Logradouro obrigatório"),
  number: z.string().min(1, "Número obrigatório"),
  complement: z.string().optional(),
  neighborhood: z.string().min(1, "Bairro obrigatório"),
  city: z.string().min(1, "Cidade obrigatória"),
  state: z.string().min(2, "Estado obrigatório"),
});

type FormData = z.infer<typeof addressSchema>;

type State = {
  modalOpen: boolean;
  errorModalOpen: boolean;
  loading: boolean;
  editable: boolean;
};

export default function usePaymentAddressForm(navigate: NavigateFunction) {
  const dispatch = useDispatch();

  const [ui, setUi] = useState<State>({
    modalOpen: false,
    errorModalOpen: false,
    loading: false,
    editable: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
    setValue,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(addressSchema),
    mode: "onChange",
    defaultValues: {
      zipcode: "",
      streetName: "",
      number: "",
      complement: "",
      neighborhood: "",
      city: "",
      state: "",
    },
  });

  const zipcode = watch("zipcode");

  const toggleModal = useCallback(() => {
    setUi((prev) => ({ ...prev, modalOpen: !prev.modalOpen }));
  }, []);

  const toggleErrorModal = useCallback(() => {
    setUi((prev) => ({ ...prev, errorModalOpen: !prev.errorModalOpen }));
  }, []);

  const handleUpdateCallback = useCallback(
    (success: boolean) => {
      setUi((prev) => ({ ...prev, loading: false }));
      if (!success) {
        setUi((prev) => ({ ...prev, errorModalOpen: true }));
        return;
      }
      navigate("/payment");
    },
    [navigate]
  );

  const onSubmit = useCallback(
    (data: FormData) => {
      if (ui.loading) return;

      setUi((prev) => ({ ...prev, loading: true, errorModalOpen: false }));
      dispatch(
        updateAddress({
          address: {
            zipcode: data.zipcode,
            streetName: data.streetName,
            number: data.number,
            neighborhood: data.neighborhood,
            city: data.city,
            stateName: data.state,
            complement: data.complement,
          },
          callback: handleUpdateCallback,
        })
      );
    },
    [ui.loading, dispatch, handleUpdateCallback]
  );

  const handleSearchAddress = useCallback(
    (success: boolean, address?: Props["address"]) => {
      setUi((prev) => ({ ...prev, loading: false }));

      setValue("streetName", address?.endereco ?? "");
      setValue("city", address?.cidade ?? "");
      setValue("state", address?.uf ?? "");
      setValue("neighborhood", address?.bairro ?? "", { shouldValidate: true });

      if (!success || !getIsValidString(address?.estado)) {
        setUi((prev) => ({ ...prev, editable: true, modalOpen: true }));
        return;
      }

      setUi((prev) => ({
        ...prev,
        editable: !getIsValidString(address?.endereco),
      }));
      clearErrors();
    },
    [clearErrors, setValue]
  );

  useEffect(() => {
    if (zipcode && zipcode.length === 9) {
      setUi((prev) => ({ ...prev, loading: true }));
      dispatch(
        searchAddress({
          zipcode,
          callback: handleSearchAddress,
        })
      );
    }
  }, [zipcode, dispatch, handleSearchAddress]);

  return {
    toggleModal,
    isVisibleModal: ui.modalOpen,
    toggleErrorModal,
    isVisibleErrorModal: ui.errorModalOpen,
    isLoading: ui.loading,
    isDisabled: !isValid || ui.loading,
    register,
    handleSubmit: handleSubmit(onSubmit),
    errors,
    editable: ui.editable,
  };
}
